import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { DoorStatusValue, GroupRef } from '../api/types'
import { de } from '../i18n/de'
import { ApiError } from '../api/client'

/** Gruppenchip. Farbe ist nie der einzige Träger — der Name steht immer dabei. */
export function GroupChip({ group }: { group: GroupRef }) {
  return (
    <span className="chip" style={{ color: group.color }}>
      <span>{group.name}</span>
    </span>
  )
}

export function GroupChips({ groups, hidden = 0, max = 3 }: {
  groups: GroupRef[]
  hidden?: number
  max?: number
}) {
  if (groups.length === 0 && hidden === 0) {
    return <span className="faint">{de.doorcodes.noGroup}</span>
  }

  const shown = groups.slice(0, max)
  const rest = groups.length - shown.length

  return (
    <>
      {shown.map(group => <GroupChip key={group.id} group={group} />)}
      {rest > 0 && (
        <span className="faint xs" title={groups.slice(max).map(g => g.name).join(', ')}>
          {' '}+{rest}
        </span>
      )}
      {hidden > 0 && (
        <span className="faint xs" title={de.doorcodes.hiddenGroups.replace('{count}', String(hidden))}>
          {' '}+{hidden} ⓘ
        </span>
      )}
    </>
  )
}

/**
 * Türstatus. Immer Symbol und Text — „nicht aktuell" darf nicht als Ausfall gelesen werden:
 * die Tür öffnet weiter und wird nur temporär nicht aktualisiert.
 */
export function DoorStatusBadge({ status, lastSeenAt }: {
  status: DoorStatusValue
  lastSeenAt?: string | null
}) {
  if (status === 'live') {
    return <span className="status status-ok">● {de.doors.statusLive}</span>
  }
  if (status === 'waiting') {
    return <span className="status status-off" title={de.doors.waitingExplain}>◌ {de.doors.statusWaiting}</span>
  }
  if (status === 'inactive') {
    return <span className="status status-off">⊘ {de.doors.statusInactive}</span>
  }
  return (
    <span className="status status-warn" title={de.doors.staleExplain}>
      ⚠ {de.doors.statusStale}{lastSeenAt ? ` · ${relative(lastSeenAt)}` : ''}
    </span>
  )
}

/**
 * Maskierter Türcode (Entscheidung F8). Klick deckt auf, nach 30 Sekunden wieder verdeckt,
 * immer nur einer gleichzeitig — ein Bildschirmfoto der Liste ist damit nie eine
 * Codesammlung. Kopieren geht auch ohne Aufdecken.
 */
let revealToken = 0
const revealListeners = new Set<(token: number) => void>()

export function MaskedCode({ code, big = false, onCopy }: {
  code: string | null
  big?: boolean
  onCopy?: () => void
}) {
  const [token, setToken] = useState<number | null>(null)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    const listener = (active: number) => {
      // Ein anderer Code wurde aufgedeckt: diesen sofort verdecken.
      setToken(current => (current !== null && current !== active ? null : current))
    }
    revealListeners.add(listener)
    return () => { revealListeners.delete(listener) }
  }, [])

  useEffect(() => {
    const hide = () => setToken(null)
    document.addEventListener('visibilitychange', hide)
    return () => {
      document.removeEventListener('visibilitychange', hide)
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [])

  if (!code) return <span className="faint">{de.app.none}</span>

  const open = token !== null
  const formatted = code.replace(/^(\d{3})(\d{3})$/, '$1 $2')

  const toggle = () => {
    if (timer.current) window.clearTimeout(timer.current)
    if (open) {
      setToken(null)
      return
    }
    const next = ++revealToken
    setToken(next)
    revealListeners.forEach(listener => listener(next))
    timer.current = window.setTimeout(() => setToken(null), 30_000)
  }

  const copy = (event: React.MouseEvent) => {
    event.stopPropagation()
    void navigator.clipboard?.writeText(code)
    onCopy?.()
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <button
        type="button"
        className={`codemask${open ? ' open' : ''}${big ? ' big' : ''}`}
        onClick={event => { event.stopPropagation(); toggle() }}
        title={open ? 'Wird in 30 Sekunden wieder verdeckt' : 'Klicken zum Aufdecken'}
        aria-label={open ? 'Türcode verdecken' : 'Türcode aufdecken'}
      >
        {open ? formatted : '••• •••'}
        {open && <span className="ring">◕</span>}
      </button>
      <button type="button" className="copybtn" onClick={copy} title={de.app.copy}>⧉</button>
    </span>
  )
}

export function Card({ title, actions, children, className }: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`card${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <div className="card-h">
          {title}
          <div className="spacer" />
          {actions}
        </div>
      )}
      <div className="card-b">{children}</div>
    </div>
  )
}

export function Alert({ kind, children, actions }: {
  kind: 'info' | 'warning'
  children: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className={`alert alert-${kind}`}>
      <span className="ai">{kind === 'warning' ? '⚠' : 'ⓘ'}</span>
      <div>{children}</div>
      {actions && <><div className="spacer" />{actions}</>}
    </div>
  )
}

export function Field({ label, help, error, children }: {
  label: string
  help?: ReactNode
  error?: string | null
  children: ReactNode
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {error ? <div className="help" style={{ color: 'var(--danger)' }}>{error}</div>
             : help ? <div className="help">{help}</div> : null}
    </div>
  )
}

export function Toast({ message, kind, onDone }: {
  message: string
  kind?: 'ok' | 'warn'
  onDone: () => void
}) {
  useEffect(() => {
    const handle = window.setTimeout(onDone, 4500)
    return () => window.clearTimeout(handle)
  }, [message, onDone])

  return (
    <div className="toast" style={kind === 'warn' ? { borderLeftColor: 'var(--warning)' } : undefined}
         role="status" aria-live="polite">
      <span style={{ color: kind === 'warn' ? 'var(--warning)' : 'var(--success)' }}>
        {kind === 'warn' ? '⚠' : '✓'}
      </span>
      <span>{message}</span>
    </div>
  )
}

/**
 * Bestätigung mit Konsequenz: was passiert, welche Folge, Verb in der Schaltfläche.
 * Siehe design/06-designsystem.md.
 */
export function ConfirmDialog({ title, detail, confirmLabel, requireText, onConfirm, onCancel }: {
  title: string
  detail: ReactNode
  confirmLabel: string
  /** Wenn gesetzt, muss der Text eingegeben werden — nur beim Löschen einer Organisation. */
  requireText?: string
  onConfirm: () => void
  onCancel: () => void
}) {
  const [typed, setTyped] = useState('')
  const ready = !requireText || typed.trim() === requireText

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgb(0 0 0 / .4)',
      display: 'grid', placeItems: 'center', zIndex: 60,
    }} role="dialog" aria-modal="true" aria-label={title}>
      <div className="card" style={{ width: 460, boxShadow: 'var(--shadow-2)' }}>
        <div className="card-b">
          <h3 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)' }}>{title}</h3>
          <div className="sm muted" style={{ marginBottom: 'var(--sp-4)' }}>{detail}</div>
          {requireText && (
            <Field label={`Zur Bestätigung „${requireText}" eingeben`}>
              <input type="text" value={typed} onChange={event => setTyped(event.target.value)} />
            </Field>
          )}
          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onCancel}>{de.app.cancel}</button>
            <button className="btn btn-danger" disabled={!ready} onClick={onConfirm}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <Alert kind="warning" actions={onRetry
      ? <button className="btn btn-ghost" onClick={onRetry}>{de.app.retry}</button>
      : undefined}>
      {describeError(error)}
    </Alert>
  )
}

/** Übersetzt den stabilen type-Slug; der Servertext ist nur die Rückfallebene. */
export function describeError(error: unknown): string {
  if (!(error instanceof ApiError)) return de.errors.generic

  switch (error.type) {
    case 'keytag_app_only': return de.errors.keytagAppOnly
    case 'last_admin': return de.errors.lastAdmin
    case 'self_delete': return de.errors.selfDelete
    case 'duplicate_name': return de.errors.duplicateName
    case 'invalid_code_key': return de.errors.invalidCodeKey
    case 'forbidden': return error.detail ?? de.errors.forbidden
    case 'not_found': return de.errors.notFound
    default: return error.detail ?? error.message ?? de.errors.generic
  }
}

export function Spinner({ label }: { label?: string }) {
  return <div className="sm faint" style={{ padding: 'var(--sp-4)' }}>◌ {label ?? de.app.loading}</div>
}

/** Lädt beim Scrollen nach. Ersetzt das Seitenblättern (Entscheidung F6). */
export function InfiniteFooter({ hasMore, loading, onLoadMore }: {
  hasMore: boolean
  loading: boolean
  onLoadMore: () => void
}) {
  const sentinel = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = sentinel.current
    if (!node || !hasMore) return

    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting) && !loading) onLoadMore()
    }, { rootMargin: '300px' })

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loading, onLoadMore])

  return (
    <div ref={sentinel} className="card-b faint sm"
         style={{ textAlign: 'center', borderTop: '1px solid var(--border)' }}>
      {hasMore ? `◌ ${de.app.loadingMore}` : de.app.noMore}
    </div>
  )
}

// ---- Formatierung ------------------------------------------------------------------------

export function formatDate(value: string | null | undefined): string {
  if (!value) return de.app.unlimited
  const date = new Date(value)
  return Number.isNaN(date.valueOf()) ? de.app.none : date.toLocaleDateString('de-AT')
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return de.app.none
  const date = new Date(value)
  return Number.isNaN(date.valueOf())
    ? de.app.none
    : date.toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'medium' })
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return de.app.none
  const date = new Date(value)
  return Number.isNaN(date.valueOf())
    ? de.app.none
    : date.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export function relative(value: string | null | undefined): string {
  if (!value) return de.accounts.never
  const then = new Date(value).valueOf()
  if (Number.isNaN(then)) return de.app.none

  const seconds = Math.round((Date.now() - then) / 1000)
  if (seconds < 60) return 'vor wenigen Sekunden'
  if (seconds < 3600) return `vor ${Math.floor(seconds / 60)} Minuten`
  if (seconds < 86_400) return `vor ${Math.floor(seconds / 3600)} Stunden`
  return `vor ${Math.floor(seconds / 86_400)} Tagen`
}

/** Tage bis zum Ablauf, null bei unbefristet. */
export function daysUntil(value: string | null): number | null {
  if (!value) return null
  const then = new Date(value).valueOf()
  if (Number.isNaN(then)) return null
  return Math.ceil((then - Date.now()) / 86_400_000)
}
